import { useEffect, useState } from "react";
import { useLocale } from "@/shared/i18n/LocaleContext";

import styles from "./PrivacyBanner.module.css";

type MarketingChoice = "granted" | "denied" | "unset";

interface PrivacyApi {
  get(): { analytics: "enabled" | "objected"; marketing: MarketingChoice };
  set(choice: { marketing?: MarketingChoice }): void;
  forget(): void;
}

function privacyApi(): PrivacyApi | undefined {
  return (window as Window & { auPrivacy?: PrivacyApi }).auPrivacy;
}

export function PrivacyBanner() {
  const { locale } = useLocale();
  const isEn = locale === "en";
  const [visible, setVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const sync = () => {
      const privacy = privacyApi();
      if (!privacy) return false;
      setReady(true);
      setVisible(privacy.get().marketing === "unset");
      return true;
    };
    const openPreferences = () => {
      setVisible(true);
      setPreferencesOpen(true);
    };
    let timer: number | undefined;
    if (!sync()) {
      timer = window.setInterval(() => {
        if (sync() && timer) window.clearInterval(timer);
      }, 100);
    }
    window.addEventListener("autouprivacyopen", openPreferences);
    return () => {
      if (timer) window.clearInterval(timer);
      window.removeEventListener("autouprivacyopen", openPreferences);
    };
  }, []);

  if (!visible) return null;

  const decide = (
    marketing: Extract<MarketingChoice, "granted" | "denied">,
  ) => {
    privacyApi()?.set({ marketing });
    setVisible(false);
    setPreferencesOpen(false);
  };

  return (
    <aside
      className={styles.banner}
      aria-label={isEn ? "Privacy preferences" : "Preferências de privacidade"}
    >
      <div>
        <p className={styles.title}>
          {isEn ? "Cookie preferences" : "Preferências de cookies"}
        </p>
        <p className={styles.text}>
          {isEn
            ? "We use cookies to improve your experience, analyze site usage, and personalize content. Read our "
            : "Utilizamos cookies para melhorar sua experiência, analisar o uso do site e personalizar conteúdos. Leia nossa "}
          <a
            href="/privacidade/declaracao-de-privacidade.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {isEn ? "Privacy Policy" : "Política de Privacidade"}
          </a>
          .
        </p>
      </div>
      {preferencesOpen ? (
        <div className={styles.actions}>
          <button
            className={styles.outline}
            type="button"
            disabled={!ready}
            onClick={() => {
              privacyApi()?.forget();
              setVisible(false);
            }}
          >
            {isEn ? "Disable site metrics" : "Desativar métricas do site"}
          </button>
          <button
            className={styles.manage}
            type="button"
            onClick={() => setPreferencesOpen(false)}
          >
            {isEn ? "Close" : "Fechar"}
          </button>
        </div>
      ) : (
        <div className={styles.actions}>
          <button
            className={styles.outline}
            type="button"
            disabled={!ready}
            onClick={() => decide("denied")}
          >
            {isEn ? "Decline" : "Recusar"}
          </button>
          <button
            className={styles.accept}
            type="button"
            disabled={!ready}
            onClick={() => decide("granted")}
          >
            {isEn ? "Accept all" : "Aceitar todos"}
          </button>
          <button
            className={styles.manage}
            type="button"
            onClick={() => setPreferencesOpen(true)}
          >
            {isEn ? "Preferences" : "Preferências"}
          </button>
        </div>
      )}
    </aside>
  );
}
